/**
 * My Work Availability — the pure model.
 *
 * Everything here is a total function over plain data: no fetch, no React, no
 * clock except the one passed in. That matters because the rules this file
 * encodes are the ones a member can be MISLED by — whether a leave has any
 * effect yet, whether an interval is valid, how long a session has run — and
 * a rule that can only be exercised through a rendered component is a rule
 * that gets tested loosely or not at all.
 *
 * The API shapes mirrored below are Central's read contract
 * (`domains/availability/http/availabilityReadContract.ts`). They are declared
 * locally rather than imported because the frontend does not depend on backend
 * source; any drift is caught by the staging certification, not by a type.
 */

/** 0 = Sunday … 6 = Saturday — the engine's convention, not a display order. */
export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export interface Interval {
  readonly start: string; // "HH:mm"
  readonly end: string;   // "HH:mm"
}

export interface WorkingWindowDto {
  readonly weekday: number;
  readonly start: string;
  readonly end: string;
}

export type LeaveKind = 'pto' | 'vacation' | 'sick' | 'other';
export type LeaveState = 'requested' | 'approved' | 'active' | 'ended' | 'cancelled';

export interface LeaveDto {
  readonly leaveId: string;
  readonly membershipId: string;
  readonly kind: LeaveKind;
  readonly state: LeaveState;
  readonly startDate: string; // "YYYY-MM-DD"
  readonly endDate: string;   // "YYYY-MM-DD"
  /** Partial-day window (member-local "HH:mm"); both null = whole day. */
  readonly startTime?: string | null;
  readonly endTime?: string | null;
  readonly effective: { readonly validFrom: string; readonly validTo: string | null };
}

/** Displayed Monday-first; the engine's numbering is preserved underneath. */
export const WEEKDAYS: ReadonlyArray<{ weekday: Weekday; label: string; short: string }> = [
  { weekday: 1, label: 'Monday', short: 'Mon' },
  { weekday: 2, label: 'Tuesday', short: 'Tue' },
  { weekday: 3, label: 'Wednesday', short: 'Wed' },
  { weekday: 4, label: 'Thursday', short: 'Thu' },
  { weekday: 5, label: 'Friday', short: 'Fri' },
  { weekday: 6, label: 'Saturday', short: 'Sat' },
  { weekday: 0, label: 'Sunday', short: 'Sun' },
];

/** A week as the editor holds it: weekday → its intervals (empty = day off). */
export type WeekDraft = Record<number, Interval[]>;

export function emptyWeek(): WeekDraft {
  const week: WeekDraft = {};
  for (const { weekday } of WEEKDAYS) week[weekday] = [];
  return week;
}

/**
 * Build the editor's week from the stored pattern.
 *
 * A member with no configuration gets an EMPTY week, never a guessed 9–5.
 * Inventing a default would make `expectedToWork` claim something Central was
 * never told, and the member would have no way to know the schedule they are
 * being judged against was fabricated.
 */
export function weekFromPattern(weekly: readonly WorkingWindowDto[] | undefined): WeekDraft {
  const week = emptyWeek();
  for (const w of weekly ?? []) {
    if (week[w.weekday] === undefined) continue; // ignore an out-of-range weekday
    week[w.weekday]!.push({ start: w.start, end: w.end });
  }
  for (const key of Object.keys(week)) {
    week[Number(key)]!.sort((a, b) => toMinutes(a.start) - toMinutes(b.start));
  }
  return week;
}

/** Flatten the editor's week back to the wire pattern, in a stable order. */
export function patternFromWeek(week: WeekDraft): WorkingWindowDto[] {
  const out: WorkingWindowDto[] = [];
  for (const { weekday } of WEEKDAYS) {
    for (const iv of week[weekday] ?? []) out.push({ weekday, start: iv.start, end: iv.end });
  }
  return out;
}

const HHMM = /^([01]\d|2[0-3]):([0-5]\d)$/;

export function toMinutes(hhmm: string): number {
  const m = HHMM.exec(hhmm);
  if (!m) return Number.NaN;
  return Number(m[1]) * 60 + Number(m[2]);
}

export interface ValidationIssue {
  readonly weekday: number;
  readonly index: number;
  readonly message: string;
}

/**
 * Validate the week the way the SERVER will.
 *
 * This mirrors `availabilityConfigurationWriteService`: well-formed times,
 * start strictly before end, and no overlap within a weekday. It is a courtesy
 * that lets a member fix a mistake without a round trip — it is NOT the
 * authority, and the save path still surrenders to the server's 400. Two
 * validators that disagree is a real risk, so the certification exercises both
 * the local refusal and the server refusal for the same input.
 *
 * OVERNIGHT is rejected here for the same reason the engine rejects it: a
 * window whose end is not after its start does not describe a period the V1
 * engine can evaluate (`windowCovers` returns false for `end <= start`). Saving
 * one would store a window that silently never matches — worse than refusing.
 */
export function validateWeek(week: WeekDraft): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  for (const { weekday, label } of WEEKDAYS) {
    const intervals = week[weekday] ?? [];
    intervals.forEach((iv, index) => {
      const s = toMinutes(iv.start);
      const e = toMinutes(iv.end);
      if (Number.isNaN(s) || Number.isNaN(e)) {
        issues.push({ weekday, index, message: `${label}: enter both times as HH:MM` });
        return;
      }
      if (e <= s) {
        issues.push({
          weekday,
          index,
          message:
            e === s
              ? `${label}: start and end cannot be the same time`
              : `${label}: end must be after start — overnight periods are not supported`,
        });
        return;
      }
      for (let j = 0; j < index; j += 1) {
        const other = intervals[j]!;
        const os = toMinutes(other.start);
        const oe = toMinutes(other.end);
        if (Number.isNaN(os) || Number.isNaN(oe)) continue;
        if (s < oe && os < e) {
          issues.push({ weekday, index, message: `${label}: this period overlaps another` });
          return;
        }
      }
    });
  }
  return issues;
}

// ── Leave truthfulness ────────────────────────────────────────────────────────

/**
 * Does this leave actually affect availability RIGHT NOW?
 *
 * The engine blocks on `approved` and `active` only (`BLOCKING_LEAVE_STATES`).
 * `requested` is inert: it is recorded, it is visible, and it changes nothing.
 * Displaying a requested leave as though the member were already off would be
 * the single most damaging thing this screen could do — somebody would stop
 * expecting to be routed work and still be routed work.
 */
export function affectsAvailability(state: LeaveState): boolean {
  return state === 'approved' || state === 'active';
}

/** Terminal leaves cannot be updated or cancelled — the service refuses. */
export function isTerminal(state: LeaveState): boolean {
  return state === 'ended' || state === 'cancelled';
}

export interface LeaveExplanation {
  readonly badge: string;
  /** Plain sentence stating the actual effect. Never aspirational. */
  readonly effect: string;
  readonly tone: 'pending' | 'active' | 'inactive';
}

export function explainLeave(state: LeaveState): LeaveExplanation {
  switch (state) {
    case 'requested':
      return {
        badge: 'Requested',
        effect: 'Awaiting approval — this does not affect your availability yet.',
        tone: 'pending',
      };
    case 'approved':
      return { badge: 'Approved', effect: 'You are marked unavailable for these dates.', tone: 'active' };
    case 'active':
      return { badge: 'In progress', effect: 'You are marked unavailable today.', tone: 'active' };
    case 'ended':
      return { badge: 'Ended', effect: 'This leave has finished.', tone: 'inactive' };
    case 'cancelled':
      return { badge: 'Cancelled', effect: 'This leave was cancelled and has no effect.', tone: 'inactive' };
  }
}

/** Inclusive-date range check, both bounds in the member's timezone. */
export function isUpcomingOrCurrent(leave: LeaveDto, todayIso: string): boolean {
  return leave.endDate >= todayIso && !isTerminal(leave.state);
}

export function validateLeaveDates(startDate: string, endDate: string): string | null {
  const iso = /^\d{4}-\d{2}-\d{2}$/;
  if (!iso.test(startDate) || !iso.test(endDate)) return 'Choose both a start and an end date.';
  if (endDate < startDate) return 'The end date cannot be before the start date.';
  return null;
}

// ── Durations ─────────────────────────────────────────────────────────────────

/**
 * `HH:MM:SS`, always — the same contract the header pill uses.
 *
 * Duplicated deliberately rather than imported: this page is Central's own
 * frontend and does not depend on `@roi-pros/ui-attendance` (a SuiteShell
 * package). The shared rule is the FORMAT, and it is pinned by identical
 * boundary tests on both sides rather than by a shared import that would drag
 * a React header component into a settings page.
 */
export function formatDuration(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds)) return '00:00:00';
  const secs = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(h)}:${p(m)}:${p(s)}`;
}

/** Elapsed since a SERVER instant, derived — never an accumulated counter. */
export function elapsedSince(startedAtIso: string, nowMs: number): string | null {
  const start = new Date(startedAtIso).getTime();
  if (Number.isNaN(start)) return null;
  return formatDuration((nowMs - start) / 1000);
}

export function formatMs(ms: number): string {
  return formatDuration(ms / 1000);
}

// ── Partial-day leave ────────────────────────────────────────────────────────

export function isPartialDay(leave: Pick<LeaveDto, 'startTime' | 'endTime'>): boolean {
  return Boolean(leave.startTime && leave.endTime);
}

/** "14:00:00" (what Postgres TIME reads back) → "14:00". Presentation only. */
export function trimSeconds(hhmm: string): string {
  return hhmm.length > 5 ? hhmm.slice(0, 5) : hhmm;
}

/**
 * How a leave's dates read to a person.
 *
 * A partial day shows its window, because "1 Oct" alone would describe a
 * dentist appointment exactly the way it describes a day off — which is the
 * ambiguity this whole feature exists to remove.
 */
export function describeLeaveWhen(leave: LeaveDto): string {
  if (isPartialDay(leave)) {
    return `${leave.startDate}, ${trimSeconds(leave.startTime!)}\u2013${trimSeconds(leave.endTime!)}`;
  }
  return leave.startDate === leave.endDate
    ? `${leave.startDate} (all day)`
    : `${leave.startDate} \u2192 ${leave.endDate} (all day)`;
}

/**
 * Validate a partial-day request the way the SERVER will.
 *
 * Mirrors `validateLeaveFields` and, beneath it, the database CHECK
 * constraints. Duplicated on purpose: the constraint is the guarantee, this is
 * the message a person can act on before a round trip.
 */
export function validatePartialDay(
  startDate: string,
  endDate: string,
  allDay: boolean,
  startTime: string,
  endTime: string,
): string | null {
  if (allDay) return null;
  if (!startTime || !endTime) return 'Enter both a start and an end time, or choose All day.';
  if (startDate !== endDate) {
    // Refused rather than guessed: is 09:00 the first morning, or every
    // morning? The server refuses this too.
    return 'A timed request must start and end on the same day. For several days, choose All day.';
  }
  if (endTime <= startTime) return 'The end time must be after the start time.';
  return null;
}
